/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection, Geometry } from 'geojson';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  drawingToolActions,
  layerActions,
  PUBLIC_WARNING_DRAW_TOOL_ID,
} from '@opengeoweb/store';
import { LayerType } from '@opengeoweb/webmap';
import {
  defaultDelete,
  defaultPolygon,
  emptyGeoJSON,
} from '@opengeoweb/webmap-react';
import { useWarningsTranslation } from '../../utils/i18n';

const getDrawLayerId = (mapId: string): string =>
  `drawLayer-${PUBLIC_WARNING_DRAW_TOOL_ID}-${mapId}`;

interface UsePublicWarningMapDrawProps {
  linkedMapIds: string[];
  currentGeoJSON?: FeatureCollection<Geometry>;
  isEnabled?: boolean;
  onGeometryChange: (geoJSON: FeatureCollection<Geometry>) => void;
  onGeometryClear: () => void;
}

interface UsePublicWarningMapDrawResult {
  onDrawPolygon: () => void;
  onDeletePolygon: () => void;
  isDrawPolygonActive: boolean;
  isDrawingModeActive: boolean;
}

export const usePublicWarningMapDraw = ({
  linkedMapIds,
  currentGeoJSON,
  isEnabled = true,
  onGeometryChange,
  onGeometryClear,
}: UsePublicWarningMapDrawProps): UsePublicWarningMapDrawResult => {
  const { t } = useWarningsTranslation();
  const dispatch = useDispatch();
  const prevActiveDrawModeRef = React.useRef<string>('');
  const linkedMapIdsRef = React.useRef<string[]>(linkedMapIds);

  React.useEffect(() => {
    const prevMapIds = linkedMapIdsRef.current;
    const removedMapIds = prevMapIds.filter((id) => !linkedMapIds.includes(id));
    removedMapIds.forEach((mapId) => {
      dispatch(
        layerActions.layerDelete({
          mapId,
          layerId: getDrawLayerId(mapId),
        }),
      );
    });
    linkedMapIdsRef.current = linkedMapIds;
  }, [linkedMapIds, dispatch]);

  const activeMapId = linkedMapIds[0];
  const activeDrawLayerId = activeMapId ? getDrawLayerId(activeMapId) : '';

  const activeDrawModeId = useSelector(
    (store: CoreAppStore) =>
      store.drawingtools?.entities[PUBLIC_WARNING_DRAW_TOOL_ID]
        ?.activeDrawModeId || '',
  );

  React.useEffect(() => {
    if (linkedMapIds.length > 0 || activeDrawModeId === '') {
      return;
    }

    dispatch(
      drawingToolActions.changeDrawToolMode({
        drawToolId: PUBLIC_WARNING_DRAW_TOOL_ID,
        drawModeId: '',
      }),
    );
  }, [activeDrawModeId, dispatch, linkedMapIds]);

  const activeDrawLayerGeoJSON = useSelector((store: CoreAppStore) =>
    activeDrawLayerId
      ? (store.layers?.byId[activeDrawLayerId]?.geojson as
          | FeatureCollection<Geometry>
          | undefined)
      : undefined,
  );

  const layersById = useSelector(
    (store: CoreAppStore) => store.layers?.byId || {},
  );

  const ensureLayers = React.useCallback((): void => {
    if (!isEnabled) {
      return;
    }
    linkedMapIds.forEach((mapId) => {
      const layerId = getDrawLayerId(mapId);
      if (!layersById[layerId]) {
        dispatch(
          layerActions.addLayer({
            mapId,
            layer: {
              geojson: currentGeoJSON || emptyGeoJSON,
              layerType: LayerType.featureLayer,
            },
            layerId,
            origin: 'PublicWarningsForm',
          }),
        );
      } else if (currentGeoJSON && !activeDrawModeId) {
        const existingGeoJSON = layersById[layerId].geojson as
          | FeatureCollection<Geometry>
          | undefined;
        if (existingGeoJSON !== currentGeoJSON) {
          dispatch(
            layerActions.layerChangeGeojson({
              layerId,
              geojson: currentGeoJSON,
            }),
          );
        }
      }
    });
  }, [
    linkedMapIds,
    layersById,
    dispatch,
    currentGeoJSON,
    activeDrawModeId,
    isEnabled,
  ]);

  const ensureDrawTool = React.useCallback((): void => {
    dispatch(
      drawingToolActions.registerDrawTool({
        drawToolId: PUBLIC_WARNING_DRAW_TOOL_ID,
        defaultDrawModes: [
          {
            ...defaultPolygon,
            title: t('warning-button-draw-polygon'),
          },
          {
            ...defaultDelete,
            title: t('warning-button-delete-polygon'),
          },
        ],
      }),
    );
  }, [dispatch, t]);

  const activateTool = React.useCallback(
    (drawModeId: string): void => {
      if (!isEnabled || !activeMapId) {
        return;
      }
      ensureDrawTool();
      ensureLayers();
      dispatch(
        drawingToolActions.updateGeoJSONLayerId({
          drawToolId: PUBLIC_WARNING_DRAW_TOOL_ID,
          geoJSONLayerId: getDrawLayerId(activeMapId),
        }),
      );
      dispatch(
        drawingToolActions.changeDrawToolMode({
          drawToolId: PUBLIC_WARNING_DRAW_TOOL_ID,
          drawModeId,
        }),
      );
    },
    [isEnabled, activeMapId, ensureDrawTool, ensureLayers, dispatch],
  );

  const onDrawPolygon = React.useCallback((): void => {
    activateTool(defaultPolygon.drawModeId);
  }, [activateTool]);

  const onDeletePolygon = React.useCallback((): void => {
    if (!isEnabled) {
      return;
    }
    activateTool(defaultDelete.drawModeId);
    linkedMapIds.forEach((mapId) => {
      dispatch(
        layerActions.layerChangeGeojson({
          layerId: getDrawLayerId(mapId),
          geojson: emptyGeoJSON,
        }),
      );
    });
    onGeometryClear();
  }, [activateTool, dispatch, isEnabled, linkedMapIds, onGeometryClear]);

  React.useEffect(() => {
    if (!isEnabled) {
      return;
    }
    ensureLayers();
  }, [ensureLayers, isEnabled]);

  React.useEffect(() => {
    if (!isEnabled) {
      return;
    }
    const hadActiveDrawMode = prevActiveDrawModeRef.current !== '';
    const exitedDrawMode = hadActiveDrawMode && activeDrawModeId === '';
    if (!exitedDrawMode || !activeMapId) {
      prevActiveDrawModeRef.current = activeDrawModeId;
      return;
    }

    const resultingGeoJSON = activeDrawLayerGeoJSON || emptyGeoJSON;
    if (resultingGeoJSON.features.length === 0) {
      onGeometryClear();
    } else {
      onGeometryChange(resultingGeoJSON);
      linkedMapIds
        .filter((mapId) => mapId !== activeMapId)
        .forEach((mapId) => {
          dispatch(
            layerActions.layerChangeGeojson({
              layerId: getDrawLayerId(mapId),
              geojson: resultingGeoJSON,
            }),
          );
        });
    }

    prevActiveDrawModeRef.current = activeDrawModeId;
  }, [
    activeMapId,
    activeDrawLayerGeoJSON,
    activeDrawModeId,
    dispatch,
    isEnabled,
    linkedMapIds,
    onGeometryChange,
    onGeometryClear,
  ]);

  React.useEffect(() => {
    return (): void => {
      dispatch(
        drawingToolActions.unregisterDrawTool({
          drawToolId: PUBLIC_WARNING_DRAW_TOOL_ID,
        }),
      );
      linkedMapIdsRef.current.forEach((mapId) => {
        dispatch(
          layerActions.layerDelete({
            mapId,
            layerId: getDrawLayerId(mapId),
          }),
        );
      });
    };
  }, [dispatch]);

  return {
    onDrawPolygon,
    onDeletePolygon,
    isDrawPolygonActive: activeDrawModeId === defaultPolygon.drawModeId,
    isDrawingModeActive: activeDrawModeId !== '',
  };
};
